import * as React from "react";
import { cn } from "./utils";
import { X } from "lucide-react";

interface ToastItem {
  id: string;
  message: string;
  type: "success" | "error" | "info" | "warning";
  duration?: number;
}

const toastSubscribers = new Set<(toasts: ToastItem[]) => void>();
let toasts: ToastItem[] = [];

function notifySubscribers() {
  toastSubscribers.forEach((callback) => callback([...toasts]));
}

let idCounter = 0;

export const toast = {
  success: (message: string, options?: { duration?: number }) => {
    const id = `toast-${++idCounter}`;
    const newToast: ToastItem = {
      id,
      message,
      type: "success",
      duration: options?.duration || 3000,
    };
    toasts = [...toasts, newToast];
    notifySubscribers();

    setTimeout(() => {
      toasts = toasts.filter((t) => t.id !== id);
      notifySubscribers();
    }, newToast.duration);
  },
  error: (message: string, options?: { duration?: number }) => {
    const id = `toast-${++idCounter}`;
    const newToast: ToastItem = {
      id,
      message,
      type: "error",
      duration: options?.duration || 3000,
    };
    toasts = [...toasts, newToast];
    notifySubscribers();

    setTimeout(() => {
      toasts = toasts.filter((t) => t.id !== id);
      notifySubscribers();
    }, newToast.duration);
  },
  info: (message: string, options?: { duration?: number }) => {
    const id = `toast-${++idCounter}`;
    const newToast: ToastItem = {
      id,
      message,
      type: "info",
      duration: options?.duration || 3000,
    };
    toasts = [...toasts, newToast];
    notifySubscribers();

    setTimeout(() => {
      toasts = toasts.filter((t) => t.id !== id);
      notifySubscribers();
    }, newToast.duration);
  },
  warning: (message: string, options?: { duration?: number }) => {
    const id = `toast-${++idCounter}`;
    const newToast: ToastItem = {
      id,
      message,
      type: "warning",
      duration: options?.duration || 3000,
    };
    toasts = [...toasts, newToast];
    notifySubscribers();

    setTimeout(() => {
      toasts = toasts.filter((t) => t.id !== id);
      notifySubscribers();
    }, newToast.duration);
  },
};

export function Toaster() {
  const [currentToasts, setCurrentToasts] = React.useState<ToastItem[]>([]);

  React.useEffect(() => {
    const callback = (newToasts: ToastItem[]) => {
      setCurrentToasts(newToasts);
    };
    toastSubscribers.add(callback);
    return () => {
      toastSubscribers.delete(callback);
    };
  }, []);

  const removeToast = (id: string) => {
    toasts = toasts.filter((t) => t.id !== id);
    notifySubscribers();
  };

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
      {currentToasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            "min-w-[300px] rounded-lg border p-4 shadow-lg flex items-start gap-3 animate-in slide-in-from-top-full",
            toast.type === "success" && "bg-green-50 border-green-200 text-green-900",
            toast.type === "error" && "bg-red-50 border-red-200 text-red-900",
            toast.type === "info" && "bg-blue-50 border-blue-200 text-blue-900",
            toast.type === "warning" && "bg-yellow-50 border-yellow-200 text-yellow-900",
          )}
        >
          <div className="flex-1 text-sm">{toast.message}</div>
          <button
            onClick={() => removeToast(toast.id)}
            className="text-current opacity-70 hover:opacity-100"
          >
            <X className="size-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
